// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import {
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import * as THREE from 'three';

import { PointCloudScene, type ColorMode, type PageBuffers, type PointSizeUnit } from './PointCloudScene';
import type { CameraProjection, LightingState, ViewPreset } from './cameraViews';
import { ClippingBoxLayer } from './ClippingBoxLayer';
import { parseCssColor, type ColorLut, type GhostStyle, type Rgb } from './colorLut';
import { hitTest } from './instanceIndex';
import { ElementsScene } from './ElementsScene';
import { MeshScene } from './MeshScene';
import { prepareSnapMeshes, probeSurface, type SurfaceProbe } from './snapProbe';
import { rayToPlaneZ } from './panorama';
import { PanoramaScene, type PanoramaMarker } from './PanoramaScene';
import { PoseGraphScene, type SelectedEdge } from './PoseGraphScene';
import { SplatScene } from './SplatScene';
import { useCloudStream, type CloudStreamState } from './useCloudStream';
import styles from './Viewport.module.css';
import { recalledCamera, rememberCamera } from './viewerCache';
import { api } from '../api/client';
import type { BuildingElement, PoseGraph, PoseGraphEdgeType } from '../api/types';
import type { ProjectionPlane } from './posegraphLayer';

export type { SelectedEdge };

/** A world-space corner, metres. */
export interface WorldPoint {
  x: number;
  y: number;
  z: number;
}

/**
 * Clickable screen targets (the Ground's instance centroids, Overblik's room
 * centroids). Projected per pointer event — never per frame — so a few hundred
 * targets cost nothing while the camera is still.
 */
export interface PointerHits {
  /** World xyz, stride 3; `NaN` = not clickable right now. */
  points: Float32Array;
  /** Pick radius in CSS pixels; default 14 (the prototype's). */
  radiusPx?: number;
  /** The target under the pointer changed (null = none). */
  onHover: (index: number | null) => void;
  /** A click (not a drag) on a target, or on nothing (null). */
  onClick: (index: number | null, shift: boolean) => void;
}

/** One room name drawn over the model at a world position. */
export interface RoomLabel {
  /** Stable React key. */
  key: string;
  name: string;
  world: [number, number, number];
  /** Outside the focused room: drawn faint. */
  dim: boolean;
}

/** Imperative access for a host that needs the scene (framing, capture). Every call is a no-op before the scene exists. */
export interface ViewportHandle {
  /** World coordinates of scene 0,0,0, or null before any content. */
  worldOrigin(): Rgb | null;
  /** Scene-local bounds of everything loaded (the clipping box's frame), or null. */
  sceneBounds(): { min: WorldPoint; max: WorldPoint } | null;
  /** Frame a world-space box from the current view direction. */
  frameWorldBox(min: WorldPoint, max: WorldPoint): void;
  /** Frame everything loaded. */
  frameAll(): void;
  /** Render now and read the frame back as a PNG. */
  capture(): Promise<Blob | null>;
  /**
   * World points (xyz, stride 3) → canvas CSS pixels (xy, stride 2; `NaN`
   * off screen). `clip: false` ignores the clipping box and plan section —
   * for chrome over the model (the plan's spaces and markers). Pass world
   * coordinates as a `Float64Array`: the scene origin is taken off before
   * anything is rounded, so georeferenced (UTM) coordinates keep their
   * millimetres.
   */
  projectWorld(world: Float32Array | Float64Array, options?: { clip?: boolean }): Float32Array;
  /** Where canvas pixel (x, y) meets the horizontal plane at world height `z`, or null. */
  planPoint(x: number, y: number, z: number): [number, number, number] | null;
  /**
   * What lies under canvas pixel (x, y) for snapping (the measure tools):
   * the pick ray, the nearest cloud point within `radiusPx` and the mesh
   * hit with its triangle's corners, in world coordinates; null before any
   * content (`snapProbe.ts`).
   */
  probeSnap(x: number, y: number, radiusPx: number): SurfaceProbe | null;
  /** Build what `probeSnap` needs ahead of the first call (the meshes' BVHs). */
  prepareSnap(): void;
  /** Call `listener` whenever the camera moves; returns the unsubscribe. */
  onCameraChange(listener: () => void): () => void;
  /** Call `listener` when the user starts moving the camera (not a programmatic move); returns the unsubscribe. */
  onUserCamera(listener: () => void): () => void;
}

/** The pick radius when `PointerHits.radiusPx` is not given (prototype: 14 px). */
const DEFAULT_HIT_RADIUS = 14;

/** Progress of the PLY mesh layer, as the panel reports it (#265, review pt 2). */
export interface MeshLayerState {
  loading: boolean;
  error: Error | null;
}

/** The typed building model's per-element solids (frontend-r22.27, `ElementsScene`). */
export interface ViewportElements {
  /** Every element; replaced as a whole when the list changes. */
  list: readonly BuildingElement[];
  /** Categories not drawn (Geometri's toggles). */
  hidden: ReadonlySet<string>;
  visible: boolean;
  wireframe: boolean;
  /** The selected element's guid, drawn in the accent colour. */
  selected: string | null;
}

/** One mesh the viewport should render. */
export interface ViewportMesh {
  /** Mesh name — also the scene layer id. */
  name: string;
  /** Where the renderer fetches the PLY blob. */
  url: string;
  visible: boolean;
  wireframe: boolean;
  /** Surface colour token when the geometry carries no colour; default `--mesh-surface`. */
  colorToken?: string;
  /** Fill what a section plane cuts open (`MeshScene.setCaps`); default off. */
  caps?: boolean;
  /** Bumped when the stored mesh was rewritten under the same URL (a re-import): downloads it again. */
  revision?: number;
  /** The mesh's identity from the server's list (`viewerCache.meshIdentity`), for the cache key; read at load. */
  identity?: string;
}

/** Progress of the Gaussian-splat layer, as the panel reports it. */
export interface SplatLayerState {
  loading: boolean;
  /** 0..1 while downloading, null before the first progress callback. */
  fraction: number | null;
  loaded: boolean;
  error: Error | null;
}

export interface ViewportSplat {
  /** The splat's name in the project — also the scene layer id. */
  name: string;
  /** Where the renderer fetches the INRIA `.ply`. */
  url: string;
  visible: boolean;
}

export interface ViewportLayer {
  /** Cloud name — also the scene layer id. */
  cloud: string;
  visible: boolean;
  /**
   * `Label` cloud to colour this layer by, or null.
   *
   * The caller is responsible for only supplying one that is genuinely
   * index-aligned with this layer; see `ViewportPage`.
   */
  labelCloud?: string | null;
  /**
   * Several index-aligned `Label` clouds zipped at once (the Ground:
   * `instances`, `rooms`). Wins over `labelCloud`; the first one is also the
   * `'label'` mode's source. Same alignment rule as `labelCloud`.
   */
  labelClouds?: string[];
  /**
   * Bumped when this layer's cloud or label cloud was rewritten on the
   * server (`clouds.changed`): the layer is dropped and streamed again.
   */
  revision?: number;
  /** Overview budget (`max_points`); the Ground's Tæthed. Default: the stream's own. */
  overviewPoints?: number;
  /** Stop after the overview (Tæthed below 100 %). */
  lodOnly?: boolean;
  /** The clouds' identity from the server's list (`viewerCache.cloudIdentity`), for the cache key. */
  identity?: string;
}

export interface ViewportProps {
  layers: ViewportLayer[];
  colorMode: ColorMode;
  pointSize: number;
  /** Metres (attenuated, the default) or screen pixels (the Ground). */
  pointSizeUnit?: PointSizeUnit;
  /** The `'lut'` colour mode's table (`colorLut.ts`). */
  colorLut?: ColorLut | null;
  /** Multiplier on `--ghost-alpha` (the Skygge setting); default 1. */
  ghostStrength?: number;
  /**
   * Opacity of ghost points (unlabelled, unowned, outside a room/storey
   * focus), 0..1; below 1 they draw see-through so labelled points behind
   * them show. Default 1 (opaque).
   */
  ghostOpacity?: number;
  /**
   * What the canvas clears to. `canvas` (default): `--color-canvas`, pinned to
   * the dark token scope — the expert viewports. `surface`: `--color-surface`
   * in the app's theme — the Ground (PLAN.md decision 5).
   */
  background?: 'canvas' | 'surface';
  /** Change it after a theme switch so token colours are re-read. */
  themeKey?: string | number;
  /** Click/hover targets; the cursor becomes a pointer over one. */
  pointerHits?: PointerHits;
  /** Room names over the model, placed on every camera change. */
  roomLabels?: RoomLabel[];
  /** Every page added to the scene, with its scene layer id (the overview has its own). */
  onPageAdded?: (layerId: string, buffers: PageBuffers) => void;
  /** A scene layer was dropped (superseded overview, restarted stream, unmount). */
  onLayerRemoved?: (layerId: string) => void;
  /** Bump to re-frame the camera on the loaded content. */
  frameToken: number;

  /** Orbit-camera projection (#443). Defaults to perspective when omitted. */
  projection?: CameraProjection;
  /** Key + fill light settings (#443). Defaults to the scene's own when omitted. */
  lighting?: LightingState;
  /**
   * A pending preset-view request (#443).
   *
   * A `{ preset, nonce }` rather than a bare preset so that asking for the same
   * view twice still re-frames: the effect keys on the object identity, which
   * the page changes by bumping the nonce on every button press.
   */
  view?: { preset: ViewPreset; nonce: number } | null;
  /**
   * The locked plan (frontend-ayw.1): orthographic, straight down, pan and
   * zoom only. Off puts back the camera and projection from before.
   */
  planLock?: boolean;
  /**
   * World height above which everything is cut away (the locked plan's
   * storey section), or null. Panorama markers are then drawn unclipped.
   */
  sectionHeight?: number | null;

  onLayerProgress?: (cloud: string, state: CloudStreamState) => void;
  /**
   * PLY mesh layers (#265, review pt 2).
   *
   * Only meshes the user has switched on belong here — the blob can be large
   * and the loader starts fetching immediately on mount.
   */
  meshes?: ViewportMesh[];
  onMeshProgress?: (name: string, state: MeshLayerState) => void;
  /** The building model's elements as solids; null/undefined draws none. */
  elements?: ViewportElements | null;
  /** Called once the elements are in the scene (true) or the list drew nothing (false). */
  onElementsShown?: (shown: boolean) => void;
  /**
   * A click on an element (its guid), or on no element (null, while
   * elements are drawn). Takes precedence over the instance/room targets.
   */
  onPickElement?: (guid: string | null) => void;

  /**
   * The Gaussian-splat layers the page has asked to be loaded (#322).
   *
   * A separate array rather than more `ViewportLayer`s: a splat is not a
   * cloud, it does not stream in pages, and it is not coloured by a label
   * source — folding it into that array would mean every field of
   * `ViewportLayer` becoming optional to describe something it does not model.
   *
   * Only splats that should actually be downloaded belong here. A splat the
   * user has never switched on must be absent, not present-and-hidden: the
   * blob is hundreds of megabytes and mounting the loader starts fetching it.
   */
  splats?: ViewportSplat[];
  onSplatProgress?: (name: string, state: SplatLayerState) => void;

  /**
   * Capture positions to mark in the orbit view (#265, Phase 5).
   *
   * Only placeable panoramas belong here — one with neither an aligned pose
   * nor a matched frame pose has no position to mark, and marking it at the
   * origin would put it somewhere the scan never was.
   */
  panoramas?: PanoramaMarker[];
  showPanoramaMarkers?: boolean;
  /**
   * The panorama to stand inside, or null for the orbit view.
   *
   * A marker plus a URL rather than an id, so this component never has to know
   * how a panorama is placed or where its image comes from.
   */
  activePanorama?: { marker: PanoramaMarker; imageUrl: string } | null;
  onPanoramaState?: (state: PanoramaLayerState) => void;
  /** A marker was clicked. */
  onPickPanorama?: (id: number) => void;

  /**
   * Pose-graph overlay (#265, review pt 4; #445).
   *
   * null while the fetch is in flight; an empty object when the project has
   * never been optimised (no edges, nodes from sensor-frame poses).
   */
  poseGraph?: PoseGraph | null;
  poseGraphVisible?: boolean;
  /** Flatten graph positions to a 2D plane for inspection (#445). */
  poseGraphProjectionPlane?: ProjectionPlane;
  /** Independent edge-type visibility (#445). */
  poseGraphEdgeTypeVisible?: Record<PoseGraphEdgeType, boolean>;
  /** Highlight edges above this residual value in amber; 0 = disabled (#445). */
  poseGraphResidualThreshold?: number;
  /** Colour nodes by connectivity degree instead of uniform grey (#445). */
  poseGraphNodeColorMode?: 'default' | 'degree';
  /** The currently selected edge (#407); null to clear. */
  poseGraphSelectedEdge?: SelectedEdge | null;
  /** Called when the user clicks an edge (or the background to deselect). */
  onPickPoseGraphEdge?: (edge: SelectedEdge | null) => void;

  /**
   * Clipping box state (#444).
   *
   * When present, a `ClippingBoxLayer` is mounted. Two independent switches:
   * `toolVisible` shows the box and its draggable face handles, `active`
   * applies six renderer-level clipping planes — once there is a box
   * (`min`/`max`, or a handle being dragged). `onBoxChange` is called on
   * drag-end so the React state holds the box.
   */
  clipping?: {
    /** The box and its face handles are shown and take the pointer. */
    toolVisible: boolean;
    /** The cut is applied whenever there is a box. */
    active: boolean;
    /** Scene-local min corner, or null for no box (the tool starts on the scene bounds). */
    min: { x: number; y: number; z: number } | null;
    /** Scene-local max corner, or null for no box. */
    max: { x: number; y: number; z: number } | null;
    onBoxChange: (min: { x: number; y: number; z: number }, max: { x: number; y: number; z: number }) => void;
  };

  /**
   * Chrome drawn over the canvas.
   *
   * A slot rather than a component, because the host element is what makes
   * `position: absolute` mean "over the viewport" — an overlay rendered beside
   * `<Viewport>` would position itself against the page instead.
   */
  overlay?: ReactNode;

  /**
   * Point-pick mode (#454).
   *
   * When true the orbit controls are suspended; the next pointer-up that did not
   * move more than 4 px picks the nearest point-cloud point and calls
   * `onPickPoint` with its world-space coordinates.  The host is responsible for
   * toggling this back off (e.g. after the panel opens) if it only wants a single
   * pick per mode-entry.
   */
  pickMode?: boolean;
  /** Called with the world-space point the user just picked (#454). */
  onPickPoint?: (point: { x: number; y: number; z: number }) => void;
  /**
   * A click (not a drag) anywhere on the canvas, in canvas CSS pixels,
   * before the markers and targets see it; return true to consume it (the
   * plan's placement and space clicks).
   */
  onCanvasClick?: (x: number, y: number, modifiers: { shift: boolean; ctrl: boolean; alt: boolean }) => boolean;
  /**
   * The scene this viewport draws into, once it exists (null on unmount):
   * for a page-owned layer that needs the camera, the canvas and the orbit
   * controls itself — the Posegraf editor's rig and gizmo (`RigScene`).
   */
  onScene?: (scene: PointCloudScene | null) => void;

  /**
   * Keep the camera across a remount (frontend-38f): on unmount the orbit
   * camera is remembered under this key (per case, `viewerCache.ts`), and the
   * next viewport with the same key starts there instead of framing the
   * content. Read once, at mount.
   */
  cameraKey?: string;
}

/** Load state of the panorama backdrop, as the page reports it. */
export interface PanoramaLayerState {
  loading: boolean;
  error: Error | null;
}

/**
 * Canvas host.
 *
 * React owns the element and the scene's lifetime; the scene owns everything
 * inside it. Nothing about the GPU state lives in component state, so a page of
 * points landing does not cause a render — see the note on `PointCloudScene`.
 */
export const Viewport = forwardRef<ViewportHandle, ViewportProps>(function Viewport({
  layers,
  colorMode,
  pointSize,
  pointSizeUnit = 'm',
  colorLut,
  ghostStrength = 1,
  ghostOpacity = 1,
  background = 'canvas',
  themeKey,
  pointerHits,
  roomLabels,
  onPageAdded,
  onLayerRemoved,
  frameToken,
  projection,
  lighting,
  view,
  planLock = false,
  sectionHeight = null,
  onLayerProgress,
  meshes,
  onMeshProgress,
  elements = null,
  onElementsShown,
  onPickElement,
  splats,
  onSplatProgress,
  panoramas,
  showPanoramaMarkers = true,
  activePanorama,
  onPanoramaState,
  onPickPanorama,
  poseGraph,
  poseGraphVisible = false,
  poseGraphProjectionPlane,
  poseGraphEdgeTypeVisible,
  poseGraphResidualThreshold,
  poseGraphNodeColorMode,
  poseGraphSelectedEdge,
  onPickPoseGraphEdge,
  clipping,
  overlay,
  pickMode = false,
  onPickPoint,
  onCanvasClick,
  onScene,
  cameraKey,
}: ViewportProps, ref) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const [scene, setScene] = useState<PointCloudScene | null>(null);
  const framedOnce = useRef(false);
  const panoramaRef = useRef<PanoramaScene | null>(null);
  const poseGraphRef = useRef<PoseGraphScene | null>(null);
  const elementsRef = useRef<ElementsScene | null>(null);

  const cameraKeyRef = useRef(cameraKey);
  useEffect(() => {
    if (!canvasRef.current) return;
    const instance = new PointCloudScene(canvasRef.current);
    const key = cameraKeyRef.current;
    const scope = api.caseId ?? null;
    if (key) {
      // Back where it was: the content (replayed from the cache) lands under
      // the remembered camera, so the first-content framing must not run.
      const snapshot = recalledCamera(scope, key);
      if (snapshot && instance.restoreCamera(snapshot)) framedOnce.current = true;
    }
    setScene(instance);
    return () => {
      if (key) rememberCamera(scope, key, instance.cameraSnapshot());
      instance.dispose();
      setScene(null);
      framedOnce.current = false;
    };
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      worldOrigin: () => scene?.worldOrigin() ?? null,
      sceneBounds: () => {
        const box = scene?.getSceneBounds();
        if (!box || box.isEmpty()) return null;
        return { min: { x: box.min.x, y: box.min.y, z: box.min.z }, max: { x: box.max.x, y: box.max.y, z: box.max.z } };
      },
      frameWorldBox: (min, max) => scene?.frameWorldBox(min, max),
      frameAll: () => scene?.frameAll(),
      capture: () => (scene ? scene.capture() : Promise.resolve(null)),
      projectWorld: (world, options) =>
        scene ? scene.projectToScreen(world, options?.clip ?? true) : new Float32Array((world.length / 3) * 2).fill(Number.NaN),
      planPoint: (x, y, z) => {
        const canvas = canvasRef.current;
        if (!scene || !canvas || canvas.clientWidth === 0 || canvas.clientHeight === 0) return null;
        const ray = scene.worldRayAt(new THREE.Vector2((x / canvas.clientWidth) * 2 - 1, -(y / canvas.clientHeight) * 2 + 1));
        return ray ? rayToPlaneZ(ray.origin, ray.dir, z) : null;
      },
      probeSnap: (x, y, radiusPx) => (scene ? probeSurface(scene, x, y, radiusPx) : null),
      prepareSnap: () => {
        if (scene) prepareSnapMeshes(scene);
      },
      onCameraChange: (listener) => (scene ? scene.onCameraChange(listener) : () => {}),
      onUserCamera: (listener) => (scene ? scene.onUserCamera(listener) : () => {}),
    }),
    [scene],
  );

  useEffect(() => {
    scene?.setColorMode(colorMode);
  }, [scene, colorMode]);

  useEffect(() => {
    scene?.setColorLut(colorLut ?? null);
  }, [scene, colorLut]);

  useEffect(() => {
    scene?.setGhostOpacity(ghostOpacity);
  }, [scene, ghostOpacity]);

  useEffect(() => {
    scene?.setPointSize(pointSize, pointSizeUnit);
  }, [scene, pointSize, pointSizeUnit]);

  // Clear colour and ghost style from the tokens, re-read on a theme switch
  // (`themeKey`). Read off the canvas so its own scope applies: pinned dark
  // for the expert viewports, the app theme for the Ground.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!scene || !canvas) return;
    const surfaceToken = background === 'surface' ? '--color-surface' : '--color-canvas';
    scene.applyBackgroundToken(surfaceToken);
    const style = getComputedStyle(canvas);
    const read = (name: string, fallback: Rgb): Rgb => parseCssColor(style.getPropertyValue(name)) ?? fallback;
    const alpha = Number.parseFloat(style.getPropertyValue('--ghost-alpha'));
    const ghostStyle: GhostStyle = {
      surface: read(surfaceToken, [0.83, 0.83, 0.81]),
      ghost: read('--color-ghost', [0.27, 0.28, 0.3]),
      alpha: (Number.isFinite(alpha) ? alpha : 0.28) * ghostStrength,
    };
    scene.setGhostStyle(ghostStyle);
  }, [scene, background, themeKey, ghostStrength]);

  useEffect(() => {
    if (projection) scene?.setProjection(projection);
  }, [scene, projection]);

  useEffect(() => {
    scene?.setPlanLock(planLock);
  }, [scene, planLock]);

  useEffect(() => {
    if (lighting) scene?.setLighting(lighting);
  }, [scene, lighting]);

  useEffect(() => {
    if (view) scene?.setView(view.preset);
  }, [scene, view]);

  useEffect(() => {
    for (const layer of layers) {
      scene?.setLayerVisible(layer.cloud, layer.visible);
      // The coarse overview is part of the same layer as far as the user is
      // concerned, so it has to follow the same toggle. Missing this would
      // leave a ghost of a hidden layer on screen until its stream finished.
      scene?.setLayerVisible(overviewLayerId(layer.cloud), layer.visible);
    }
  }, [scene, layers]);

  useEffect(() => {
    // frameToken 0 is the initial value and must not steal the automatic
    // first-content framing below.
    if (frameToken > 0) scene?.frameAll();
  }, [scene, frameToken]);

  // A page-owned layer (the Posegraf rig) gets the scene once it exists.
  const onSceneRef = useRef(onScene);
  onSceneRef.current = onScene;
  useEffect(() => {
    if (!scene) return;
    onSceneRef.current?.(scene);
    return () => onSceneRef.current?.(null);
  }, [scene]);

  // --- panorama layer ------------------------------------------------------

  useEffect(() => {
    if (!scene) return;
    const layer = new PanoramaScene(scene);
    panoramaRef.current = layer;
    return () => {
      panoramaRef.current = null;
      layer.dispose();
    };
  }, [scene]);

  // Pose-graph layer (#265 review pt 4): mount once per scene, load/clear when
  // the data changes, show/hide when the toggle changes.
  useEffect(() => {
    if (!scene) return;
    const pg = new PoseGraphScene(scene.sceneRoot(), (world) => scene.toSceneLocal(world));
    poseGraphRef.current = pg;
    return () => {
      poseGraphRef.current = null;
      pg.dispose();
    };
  }, [scene]);

  useEffect(() => {
    poseGraphRef.current?.load(poseGraph ?? null);
  }, [scene, poseGraph]);

  useEffect(() => {
    poseGraphRef.current?.setVisible(poseGraphVisible);
  }, [scene, poseGraphVisible]);

  useEffect(() => {
    poseGraphRef.current?.setProjectionPlane(poseGraphProjectionPlane ?? '3D');
  }, [scene, poseGraphProjectionPlane]);

  useEffect(() => {
    if (!poseGraphEdgeTypeVisible) return;
    for (const [type, vis] of Object.entries(poseGraphEdgeTypeVisible) as [PoseGraphEdgeType, boolean][]) {
      poseGraphRef.current?.setEdgeTypeVisible(type, vis);
    }
  }, [scene, poseGraphEdgeTypeVisible]);

  useEffect(() => {
    poseGraphRef.current?.setResidualThreshold(poseGraphResidualThreshold ?? 0);
  }, [scene, poseGraphResidualThreshold]);

  useEffect(() => {
    poseGraphRef.current?.setNodeColorMode(poseGraphNodeColorMode ?? 'default');
  }, [scene, poseGraphNodeColorMode]);

  useEffect(() => {
    poseGraphRef.current?.setSelectedEdge(poseGraphSelectedEdge ?? null);
  }, [scene, poseGraphSelectedEdge]);

  // --- room labels ----------------------------------------------------------
  //
  // HTML over the canvas, positioned imperatively (a transform per label) on
  // camera change, resize, clipping change and new labels — never through a
  // React render, which would make orbiting re-render the tree every frame.

  const labelEls = useRef<(HTMLSpanElement | null)[]>([]);
  const labelsRef = useRef(roomLabels);
  labelsRef.current = roomLabels;
  const labelFrame = useRef<number | null>(null);

  const placeLabels = useCallback(() => {
    const list = labelsRef.current;
    if (!scene || !list || list.length === 0) return;
    const world = new Float32Array(list.length * 3);
    list.forEach((label, i) => world.set(label.world, i * 3));
    const screen = scene.projectToScreen(world);
    list.forEach((_, i) => {
      const el = labelEls.current[i];
      if (!el) return;
      const x = screen[i * 2];
      const y = screen[i * 2 + 1];
      if (Number.isNaN(x) || Number.isNaN(y)) {
        el.style.visibility = 'hidden';
        return;
      }
      el.style.visibility = '';
      el.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) translate(-50%, -50%)`;
    });
  }, [scene]);

  const scheduleLabels = useCallback(() => {
    if (!labelsRef.current?.length || labelFrame.current !== null) return;
    labelFrame.current = requestAnimationFrame(() => {
      labelFrame.current = null;
      placeLabels();
    });
  }, [placeLabels]);

  useLayoutEffect(() => {
    placeLabels();
  }, [placeLabels, roomLabels]);

  useEffect(() => {
    scheduleLabels();
  }, [
    scheduleLabels,
    clipping?.toolVisible,
    clipping?.active,
    clipping?.min?.x,
    clipping?.min?.y,
    clipping?.min?.z,
    clipping?.max?.x,
    clipping?.max?.y,
    clipping?.max?.z,
  ]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => scheduleLabels());
    observer.observe(host);
    return () => {
      observer.disconnect();
      if (labelFrame.current !== null) cancelAnimationFrame(labelFrame.current);
      labelFrame.current = null;
    };
  }, [scheduleLabels]);

  // Dispatch 'rux-camera-move' whenever the orbit camera changes so that
  // tile-streaming layers can re-evaluate their visible tile sets; the room
  // labels follow the camera on the same signal.
  useEffect(() => {
    if (!scene) return;
    return scene.onCameraChange(() => {
      if (typeof window !== 'undefined')
        window.dispatchEvent(new Event('rux-camera-move'));
      scheduleLabels();
    });
  }, [scene, scheduleLabels]);

  useEffect(() => {
    panoramaRef.current?.setMarkers(panoramas ?? []);
  }, [scene, panoramas]);

  useEffect(() => {
    panoramaRef.current?.setMarkersVisible(showPanoramaMarkers);
  }, [scene, panoramas, showPanoramaMarkers]);

  useEffect(() => {
    scene?.setSectionHeight(sectionHeight);
    panoramaRef.current?.setMarkersUnclipped(sectionHeight !== null);
  }, [scene, sectionHeight]);

  // Reported through a ref for the same reason the splat loader does it: both
  // callbacks are inline closures at the call site, and depending on them
  // would tear the backdrop down and re-download it on every parent render.
  const panoramaStateRef = useRef(onPanoramaState);
  panoramaStateRef.current = onPanoramaState;

  const activeId = activePanorama?.marker.id ?? null;
  const activeUrl = activePanorama?.imageUrl ?? null;
  useEffect(() => {
    const layer = panoramaRef.current;
    if (!layer) return;
    if (activeId === null || !activeUrl || !activePanorama) {
      layer.hide();
      panoramaStateRef.current?.({ loading: false, error: null });
      return;
    }

    let cancelled = false;
    panoramaStateRef.current?.({ loading: true, error: null });
    layer
      .show(activePanorama.marker.placement, activeUrl)
      .then(() => {
        if (!cancelled) panoramaStateRef.current?.({ loading: false, error: null });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        panoramaStateRef.current?.({
          loading: false,
          error: error instanceof Error ? error : new Error(String(error)),
        });
      });

    return () => {
      cancelled = true;
    };
    // Keyed by id and URL, not by the object: the page rebuilds the marker
    // array on every render, and depending on it would reload the texture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, activeId, activeUrl]);

  // Suspend orbit controls while pick mode is active so a click does not also
  // start an orbit drag.
  useEffect(() => {
    scene?.setControlsEnabled(!pickMode);
  }, [scene, pickMode]);

  /**
   * Click-to-enter / click-to-pick.
   *
   * `pointerup` with a movement threshold rather than `click`, because a drag
   * that happens to end over a marker is an orbit, not a request to teleport
   * into it — and `click` cannot tell the two apart.
   */
  const pressedAt = useRef<{ x: number; y: number } | null>(null);
  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    pressedAt.current = { x: event.clientX, y: event.clientY };
  };

  // --- pointer hits (hover cursor, click on a target) -------------------------

  const hitsRef = useRef(pointerHits);
  hitsRef.current = pointerHits;
  const hoverIndex = useRef<number | null>(null);
  const pendingMove = useRef<{ x: number; y: number } | null>(null);
  const moveFrame = useRef<number | null>(null);

  /** The target under canvas-relative CSS pixel (x, y), projecting now. */
  const hitAt = (x: number, y: number): number | null => {
    const hits = hitsRef.current;
    if (!hits || !scene || hits.points.length === 0) return null;
    return hitTest(scene.projectToScreen(hits.points), x, y, hits.radiusPx ?? DEFAULT_HIT_RADIUS);
  };

  const setHover = (index: number | null) => {
    if (index === hoverIndex.current) return;
    hoverIndex.current = index;
    if (hostRef.current) hostRef.current.style.cursor = index === null ? '' : 'pointer';
    hitsRef.current?.onHover(index);
  };

  // Targets gone (mode switch): drop the hover and its cursor.
  useEffect(() => {
    if (!pointerHits) setHover(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointerHits]);

  useEffect(
    () => () => {
      if (moveFrame.current !== null) cancelAnimationFrame(moveFrame.current);
    },
    [],
  );

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    // Not while a button is down: that is an orbit (or a clipping drag).
    if (!hitsRef.current || event.buttons !== 0) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    pendingMove.current = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    if (moveFrame.current !== null) return;
    // At most one projection per frame, however fast the pointer moves.
    moveFrame.current = requestAnimationFrame(() => {
      moveFrame.current = null;
      const at = pendingMove.current;
      if (at) setHover(hitAt(at.x, at.y));
    });
  };

  const handlePointerLeave = () => {
    pendingMove.current = null;
    setHover(null);
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const start = pressedAt.current;
    pressedAt.current = null;
    if (!start || !scene) return;
    if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > 4) return;

    const bounds = event.currentTarget.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((event.clientX - bounds.left) / bounds.width) * 2 - 1,
      -((event.clientY - bounds.top) / bounds.height) * 2 + 1,
    );

    // Point-pick mode: raycasts the cloud geometry and calls onPickPoint (#454).
    if (pickMode && onPickPoint) {
      const world = scene.pickCloudPoint(ndc);
      if (world) onPickPoint({ x: world.x, y: world.y, z: world.z });
      return;
    }

    if (onCanvasClick) {
      const canvasRect = canvasRef.current?.getBoundingClientRect() ?? bounds;
      const modifiers = { shift: event.shiftKey, ctrl: event.ctrlKey || event.metaKey, alt: event.altKey };
      if (onCanvasClick(event.clientX - canvasRect.left, event.clientY - canvasRect.top, modifiers)) return;
    }

    // Pose-graph edge picking (#407): try to pick an edge when the graph is
    // visible and a callback is registered.
    if (onPickPoseGraphEdge && poseGraphVisible && poseGraphRef.current) {
      const raycaster = scene.raycasterAt(ndc);
      const hit = poseGraphRef.current.pickEdge(raycaster);
      onPickPoseGraphEdge(hit);
      // Don't return: let panorama picking run too so a click on empty space
      // does not block entering a panorama.  Edge pick result is reported even
      // if null (to clear a previous selection).
      if (hit) return;
    }

    // Panorama-marker picking (original behaviour).
    const layer = panoramaRef.current;
    if (layer && onPickPanorama && showPanoramaMarkers) {
      for (const hit of scene.pick(ndc, layer.markerObjects())) {
        const id = layer.panoramaIdOf(hit.object);
        if (id !== null) {
          onPickPanorama(id);
          return;
        }
      }
    }

    // Building elements (frontend-r22.27): the first one the clipping leaves on screen.
    const elementLayer = elementsRef.current;
    if (elementLayer && onPickElement && elements?.visible) {
      const guid = ElementsScene.elementOf(scene.pick(ndc, elementLayer.pickObjects()), scene.activeClippingPlanes());
      onPickElement(guid);
      if (guid) return;
    }

    // Instance / room targets (the Ground). Reported even when nothing is
    // under the pointer, so the host can treat a click on empty space.
    const hits = hitsRef.current;
    if (hits) {
      const canvasRect = canvasRef.current?.getBoundingClientRect() ?? bounds;
      hits.onClick(hitAt(event.clientX - canvasRect.left, event.clientY - canvasRect.top), event.shiftKey);
    }
  };

  const surface = background === 'surface';

  return (
    <div
      ref={hostRef}
      className={[styles.host, surface ? styles.surface : '', pickMode ? styles.pickMode : ''].filter(Boolean).join(' ')}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
    >
      {/* The expert 3D canvas is near-black in both app themes (tokens.css),
          so it is pinned to the dark token scope here rather than following
          the app theme: a token that legitimately differs per theme
          (`--label-invalid`, picked for a light panel in light mode) would
          otherwise still shout against this always-dark surface. See
          PointCloudScene's use of `resolveColorToken`/`readLabelPalette`
          against this element. The Ground (`background="surface"`) is the
          exception (PLAN.md decision 5): it clears to the page surface and
          follows the theme, so it is not pinned. */}
      <canvas ref={canvasRef} className={styles.canvas} data-theme={surface ? undefined : 'dark'} />
      {roomLabels && roomLabels.length > 0 && (
        <div className={styles.labels} aria-hidden="true">
          {roomLabels.map((label, i) => (
            <span
              key={label.key}
              ref={(el) => {
                labelEls.current[i] = el;
              }}
              className={`${styles.label}${label.dim ? ` ${styles.labelDim}` : ''}`}
            >
              {label.name}
            </span>
          ))}
        </div>
      )}
      {scene &&
        layers.map((layer) => (
          <CloudLayerLoader
            key={layer.cloud}
            scene={scene}
            layer={layer}
            onFirstContent={() => {
              if (framedOnce.current) return;
              framedOnce.current = true;
              // Follow the bounds as the rest streams in: the first page is
              // one corner of the building (frontend-r22.19).
              scene.frameAll(true);
            }}
            onProgress={onLayerProgress}
            onPageAdded={onPageAdded}
            onLayerRemoved={onLayerRemoved}
          />
        ))}
      {scene &&
        (meshes ?? []).map((mesh) => (
          <MeshLayerLoader
            key={mesh.name}
            scene={scene}
            mesh={mesh}
            themeKey={themeKey}
            onFirstContent={() => {
              if (framedOnce.current) return;
              framedOnce.current = true;
              // A model with no cloud (an imported one) frames on its first
              // mesh and follows the rest as they land.
              scene.frameAll(true);
            }}
            onProgress={(state) => onMeshProgress?.(mesh.name, state)}
          />
        ))}
      {scene && elements && (
        <ElementsLayerLoader
          scene={scene}
          elements={elements}
          themeKey={themeKey}
          layerRef={elementsRef}
          onShown={(shown) => {
            onElementsShown?.(shown);
            if (!shown || framedOnce.current) return;
            framedOnce.current = true;
            scene.frameAll(true);
          }}
        />
      )}
      {scene &&
        (splats ?? []).map((splat) => (
          <SplatLayerLoader
            key={splat.name}
            scene={scene}
            url={splat.url}
            visible={splat.visible}
            onFirstContent={() => {
              if (framedOnce.current) return;
              framedOnce.current = true;
              // Follow the bounds as the rest streams in: the first page is
              // one corner of the building (frontend-r22.19).
              scene.frameAll(true);
            }}
            onProgress={(state) => onSplatProgress?.(splat.name, state)}
          />
        ))}
      {scene && clipping && (
        <ClippingBoxLayerLoader scene={scene} clipping={clipping} />
      )}
      {overlay}
    </div>
  );
});

/**
 * Draws the building model's elements into the scene (`ElementsScene`).
 * Renders nothing. A new list rebuilds the meshes; visibility, the hidden
 * categories, wireframe and the selection are cheap updates.
 */
function ElementsLayerLoader({
  scene,
  elements,
  themeKey,
  layerRef,
  onShown,
}: {
  scene: PointCloudScene;
  elements: ViewportElements;
  themeKey?: string | number;
  layerRef: { current: ElementsScene | null };
  onShown?: (shown: boolean) => void;
}) {
  const [layer, setLayer] = useState<ElementsScene | null>(null);
  const shownRef = useRef(onShown);
  shownRef.current = onShown;

  useEffect(() => {
    const instance = new ElementsScene(scene);
    layerRef.current = instance;
    setLayer(instance);
    return () => {
      if (layerRef.current === instance) layerRef.current = null;
      instance.dispose();
      setLayer(null);
    };
  }, [scene, layerRef]);

  useEffect(() => {
    if (!layer) return;
    shownRef.current?.(layer.setElements(elements.list));
  }, [layer, elements.list]);

  useEffect(() => layer?.setHidden(elements.hidden), [layer, elements.hidden]);
  useEffect(() => layer?.setVisible(elements.visible), [layer, elements.visible]);
  useEffect(() => layer?.setWireframe(elements.wireframe), [layer, elements.wireframe]);
  useEffect(() => layer?.setSelected(elements.selected), [layer, elements.selected]);
  useEffect(() => layer?.refreshColors(), [layer, themeKey]);

  return null;
}

/**
 * Loads one PLY mesh into the scene. Renders nothing.
 *
 * Mounting starts the download; unmounting disposes the geometry and removes it
 * from the scene. Hiding is handled by `MeshScene.setVisible`, not by
 * unmounting — the blob is too large to re-download on every toggle.
 */
function MeshLayerLoader({
  scene,
  mesh,
  themeKey,
  onFirstContent,
  onProgress,
}: {
  scene: PointCloudScene;
  mesh: ViewportMesh;
  themeKey?: string | number;
  onFirstContent?: () => void;
  onProgress?: (state: MeshLayerState) => void;
}) {
  const meshRef = useRef<MeshScene | null>(null);
  const progressRef = useRef(onProgress);
  progressRef.current = onProgress;
  const firstContentRef = useRef(onFirstContent);
  firstContentRef.current = onFirstContent;

  useEffect(() => {
    let cancelled = false;
    const instance = new MeshScene(scene, mesh.colorToken);
    meshRef.current = instance;
    progressRef.current?.({ loading: true, error: null });

    instance
      .load(mesh.url, mesh.revision ?? 0, mesh.identity ?? '')
      .then(() => {
        if (cancelled) return;
        progressRef.current?.({ loading: false, error: null });
        firstContentRef.current?.();
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        progressRef.current?.({
          loading: false,
          error: error instanceof Error ? error : new Error(String(error)),
        });
      });

    return () => {
      cancelled = true;
      meshRef.current = null;
      instance.dispose();
    };
  // Re-mount when the URL changes (different mesh) or the mesh was rewritten
  // (`revision`); visibility is a separate effect.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, mesh.url, mesh.revision]);

  useEffect(() => {
    meshRef.current?.setVisible(mesh.visible);
  }, [mesh.visible]);

  useEffect(() => {
    meshRef.current?.setWireframe(mesh.wireframe);
  }, [mesh.wireframe]);

  useEffect(() => {
    if (mesh.colorToken) meshRef.current?.setColorToken(mesh.colorToken);
  }, [mesh.colorToken, themeKey]);

  useEffect(() => {
    meshRef.current?.setCaps(mesh.caps ?? false);
  }, [mesh.caps]);

  return null;
}

/**
 * Streams one layer into the scene. Renders nothing.
 *
 * A component rather than a loop inside `Viewport` so that React's own keying
 * handles the lifecycle: mounting starts a download, unmounting aborts it and
 * drops the layer's buffers. Hiding a layer does **not** unmount it — the
 * points stay resident and the toggle is instant, which is the whole reason a
 * user toggles a layer in the first place.
 */
function CloudLayerLoader({
  scene,
  layer,
  onFirstContent,
  onProgress,
  onPageAdded,
  onLayerRemoved,
}: {
  scene: PointCloudScene;
  layer: ViewportLayer;
  onFirstContent: () => void;
  onProgress?: (cloud: string, state: CloudStreamState) => void;
  onPageAdded?: (layerId: string, buffers: PageBuffers) => void;
  onLayerRemoved?: (layerId: string) => void;
}) {
  const revision = layer.revision ?? 0;
  const labelClouds = layer.labelClouds ?? (layer.labelCloud ? [layer.labelCloud] : []);
  const pageAddedRef = useRef(onPageAdded);
  pageAddedRef.current = onPageAdded;
  const removedRef = useRef(onLayerRemoved);
  removedRef.current = onLayerRemoved;

  const drop = (id: string) => {
    scene.removeLayer(id);
    removedRef.current?.(id);
  };

  const state = useCloudStream({
    cloud: layer.cloud,
    labelClouds,
    revision,
    overviewPoints: layer.overviewPoints,
    lodOnly: layer.lodOnly,
    identity: layer.identity,
    getCameraState: () => scene.getCameraState(),
    onPage: (buffers, kind) => {
      // The overview goes into a layer of its own so it can be dropped whole
      // once the full-resolution pages have covered the same ground. Its
      // points are a subset of theirs at identical coordinates, so while both
      // are resident they coincide rather than fight — no z-fighting, just a
      // scene that is complete from the first request instead of the last.
      const id = kind.overview ? overviewLayerId(layer.cloud) : layer.cloud;
      scene.addPage(id, buffers);
      scene.setLayerVisible(id, layer.visible);
      pageAddedRef.current?.(id, buffers);
      onFirstContent();
    },
    onOverviewSuperseded: () => drop(overviewLayerId(layer.cloud)),
  });

  useEffect(() => {
    onProgress?.(layer.cloud, state);
    // `onProgress` is an inline closure at the call site; including it here
    // would fire this effect every render of the parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer.cloud, state]);

  useEffect(() => {
    return () => {
      scene.removeLayer(layer.cloud);
      scene.removeLayer(overviewLayerId(layer.cloud));
      removedRef.current?.(layer.cloud);
      removedRef.current?.(overviewLayerId(layer.cloud));
    };
  }, [scene, layer.cloud]);

  // Anything that restarts the stream (above) — a new revision, another set
  // of label clouds, another density — must drop the stale pages first so the
  // fresh ones replace rather than duplicate them. Runs in the same commit as
  // the restart, before any new page can have arrived.
  const streamKey = `${revision}|${labelClouds.join('|')}|${layer.overviewPoints ?? ''}|${layer.lodOnly ? 1 : 0}`;
  const mountedKey = useRef(streamKey);
  useEffect(() => {
    if (streamKey === mountedKey.current) return;
    mountedKey.current = streamKey;
    drop(layer.cloud);
    drop(overviewLayerId(layer.cloud));
    // `drop` reads refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, layer.cloud, streamKey]);

  return null;
}

/**
 * Scene layer id for a cloud's coarse overview (#320).
 *
 * `::` cannot appear in a cloud name (they are SQL identifiers written by the
 * pipeline), so this can never collide with the layer of a real cloud.
 */
function overviewLayerId(cloud: string): string {
  return `${cloud}::overview`;
}

/**
 * Mounts the clipping box layer and drives it from React props (#444).
 *
 * Mirrors the pattern of `MeshLayerLoader`: mount once per scene, update
 * imperatively from props via effects, never re-mount for property changes.
 * The layer handles its own pointer events (only while the tool is shown)
 * and calls `onBoxChange` after each drag so React holds the box.
 */
function ClippingBoxLayerLoader({
  scene,
  clipping,
}: {
  scene: PointCloudScene;
  clipping: NonNullable<ViewportProps['clipping']>;
}) {
  const layerRef = useRef<ClippingBoxLayer | null>(null);
  const onBoxChangeRef = useRef(clipping.onBoxChange);
  onBoxChangeRef.current = clipping.onBoxChange;

  useEffect(() => {
    const layer = new ClippingBoxLayer(scene, (box) => {
      onBoxChangeRef.current({ x: box.min.x, y: box.min.y, z: box.min.z }, { x: box.max.x, y: box.max.y, z: box.max.z });
    });
    layerRef.current = layer;
    return () => {
      layerRef.current = null;
      layer.dispose();
    };
  }, [scene]);

  // The box first, then the switches: showing the tool or turning the cut on
  // must see the box React holds, not start from the scene bounds.
  useEffect(() => {
    if (clipping.min && clipping.max) {
      const box = new THREE.Box3(
        new THREE.Vector3(clipping.min.x, clipping.min.y, clipping.min.z),
        new THREE.Vector3(clipping.max.x, clipping.max.y, clipping.max.z),
      );
      layerRef.current?.setBox(box);
    } else {
      // No box: no cut, and the tool goes back to the scene bounds.
      layerRef.current?.setBox(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, clipping.min?.x, clipping.min?.y, clipping.min?.z, clipping.max?.x, clipping.max?.y, clipping.max?.z]);

  useEffect(() => {
    layerRef.current?.setToolVisible(clipping.toolVisible);
  }, [scene, clipping.toolVisible]);

  useEffect(() => {
    layerRef.current?.setActive(clipping.active);
  }, [scene, clipping.active]);

  return null;
}

/**
 * Streams the Gaussian splat into the scene. Renders nothing.
 *
 * Mirrors `CloudLayerLoader`, including the rule that hiding is not unloading:
 * the splat is one large file and re-downloading it on every toggle would make
 * the toggle useless. Unlike a cloud it arrives as a single response, so the
 * download is started once, on mount, and visibility is a separate effect.
 *
 * Mounted only for splats the page has asked for and keyed by name, so React's
 * own lifecycle handles the download and its teardown.
 */
function SplatLayerLoader({
  scene,
  url,
  visible,
  onFirstContent,
  onProgress,
}: {
  scene: PointCloudScene;
  url: string;
  visible: boolean;
  onFirstContent: () => void;
  onProgress?: (state: SplatLayerState) => void;
}) {
  const splatRef = useRef<SplatScene | null>(null);
  // Effects below read the latest callbacks without listing them as deps: both
  // are inline closures at the call site, and depending on them would tear the
  // splat down and re-download it on every render of the parent.
  const progressRef = useRef(onProgress);
  progressRef.current = onProgress;
  const firstContentRef = useRef(onFirstContent);
  firstContentRef.current = onFirstContent;

  useEffect(() => {
    let cancelled = false;
    const instance = new SplatScene(scene, {
      onProgress: (fraction) =>
        progressRef.current?.({ loading: true, fraction, loaded: false, error: null }),
    });
    splatRef.current = instance;
    progressRef.current?.({ loading: true, fraction: null, loaded: false, error: null });

    instance
      .load(url)
      .then(() => {
        if (cancelled) return;
        progressRef.current?.({ loading: false, fraction: 1, loaded: true, error: null });
        firstContentRef.current();
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        progressRef.current?.({
          loading: false,
          fraction: null,
          loaded: false,
          error: error instanceof Error ? error : new Error(String(error)),
        });
      });

    return () => {
      cancelled = true;
      splatRef.current = null;
      // Fire-and-forget: `dispose` is async because the library's is, but
      // React's cleanup is not, and nothing here waits on the teardown.
      void instance.dispose();
    };
  }, [scene, url]);

  useEffect(() => {
    splatRef.current?.setVisible(visible);
  }, [visible]);

  return null;
}
